import { Component, OnInit, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { filter, switchMap, tap } from 'rxjs/operators';
import { AppUserService } from '@ceres/shared/services';
import { AppSettingsService } from '@ceres/features/admin-general';
import { TasksService } from './shared/services/tasks.service';
import { ThemeService } from './shared/services/theme.service';
import { EmployeeSettingsService } from './shared/services/employee-settings.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService } from './auth/services/auth.service';
import { defer, forkJoin, of } from 'rxjs';
import { GlobalNotificationsBannerService } from './layout/services/global-notifications-banner.service';
import { Router } from '@angular/router';

@Component({
    selector: 'ceres-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AppComponent implements OnInit {
    protected readonly appUserService = inject(AppUserService);
    protected readonly authService: AuthService = inject(AuthService);
    protected readonly bannerService = inject(GlobalNotificationsBannerService);
    private readonly appSettings = inject(AppSettingsService);
    private readonly themeService = inject(ThemeService);
    private readonly taskService = inject(TasksService);
    private readonly employeeSettingsService = inject(EmployeeSettingsService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly router = inject(Router);

    public isAppInitialProcessLoaded = false;

    public get canRenderApplication(): boolean {
        return (
            !this.authService.isAuthLoading() &&
            (this.isAppInitialProcessLoaded || this.router.url.startsWith('/unauthorized'))
        );
    }

    public ngOnInit(): void {
        this.setupUserLoadedListener();
    }

    private setupUserLoadedListener(): void {
        this.appUserService.loggedInUser$
            .pipe(
                filter((user) => !!user),
                switchMap((user) => {
                    const gid = user!.details.gid;
                    const userId = user!.details.id;

                    const appSettings$ = defer(() => {
                        this.appSettings.loadAppSettings();
                        return of(true);
                    });

                    const personalSettings$ = this.employeeSettingsService.getPersonalSettings(gid);
                    const persistentFilters$ =
                        this.employeeSettingsService.getPersistentFiltersForAppInitialization(gid);
                    const myTasks$ = this.taskService.getMyTasksWithStatus(userId, 'open');

                    return forkJoin({
                        appSettings: appSettings$,
                        personalSettings: personalSettings$,
                        persistentFilters: persistentFilters$,
                        myTasks: myTasks$
                    });
                }),
                tap((result) => {
                    this.themeService.applySettings(result.personalSettings);
                    this.isAppInitialProcessLoaded = true;
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }
}
